import React, { useEffect, useState } from "react";
import { grey } from "@mui/material/colors";
import {
  Box,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Button,
} from "@mui/material";

import axios from "axios";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import moment from "moment";
import {
  ALL_PUSH_NOTIFICATIONS,
  MARK_AS_READ_PUSH_NOTIFICATIONS,
} from "../../constants";

export default function NotificationComponent() {
  const [menuEl, setMenuEl] = React.useState(null);
  const [notificationFilter, setNotificationFilter] = React.useState("all");
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState([]);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${ALL_PUSH_NOTIFICATIONS}?employeeId=${
            JSON.parse(localStorage.getItem("userData")).EmployeeId
          }&filter=${notificationFilter}`
        );

        if (response.data.success) {
          setNotifications(response.data.notifications || []);
        } else {
          setError(response.data.message || "Failed to fetch notifications");
        }
      } catch (err) {
        console.log("Request cancelled:", err.message);
      }
    };

    fetchNotifications();
  }, [notificationFilter]);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const visibleNotifications = React.useMemo(() => {
    if (notificationFilter === "unread") {
      return notifications.filter((n) => !n.isRead);
    }
    if (notificationFilter === "read") {
      return notifications.filter((n) => n.isRead);
    }
    return notifications;
  }, [notifications, notificationFilter]);

  // Handlers
  const handleNotificationOpen = (event) => {
    setMenuEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setMenuEl(null);
  };

  const handleNotificationClick = (notification) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n))
    );
  };

  const markReadStatus = async (id) => {
    try {
      const response = await fetch(`${MARK_AS_READ_PUSH_NOTIFICATIONS}${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (response.ok) {
        console.log(data.message); // "Notification marked as read"
        // Update your UI or state here
      } else {
        console.error("Server responded with an error:", data.message);
      }
    } catch (error) {
      console.error("Network or fetch error:", error);
    }
  };

  return (
    <>
      <IconButton color="inherit" onClick={handleNotificationOpen}>
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={menuEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={Boolean(menuEl)}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        onClose={handleNotificationClose}
        PaperProps={{
          elevation: 4,
          sx: {
            filter: "drop-shadow(0px 4px 12px rgba(0,0,0,0.3))",
            mt: 1.5,
            overflow: "visible",
            width: "350px",
            maxHeight: "450px",
            borderRadius: "12px",
          },
        }}
      >
        {/* Scrollable Notification Content */}
        {/* Footer filter buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-around",
            borderBottom: 1,
            borderColor: "divider",
            py: 1,
            mb: 1,
            backgroundColor: "background.paper",
          }}
        >
          {["all", "unread", "read"].map((filter) => (
            <Button
              key={filter}
              size="small"
              onClick={() => setNotificationFilter(filter)}
              variant={notificationFilter === filter ? "contained" : "text"}
              sx={{
                textTransform: "capitalize",
                fontWeight: notificationFilter === filter ? "bold" : "normal",
                color:
                  notificationFilter === filter
                    ? "primary.contrastText"
                    : "text.primary",
                minWidth: 72,
                borderRadius: 2,
                "&:hover": {
                  backgroundColor:
                    notificationFilter === filter
                      ? "primary.dark"
                      : "action.hover",
                },
              }}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </Button>
          ))}
        </Box>

        <Box sx={{ maxHeight: 350, overflowY: "auto", p: 1 }}>
          {visibleNotifications.length > 0 ? (
            visibleNotifications.map((notification) => (
              <Box
                key={notification.id}
                sx={{
                  py: 1.25,
                  px: 2,
                  bgcolor: !notification.isRead ? "action.selected" : "inherit",
                  borderRadius: 2,
                  mb: 1,
                  cursor: "pointer",
                  transition: "background-color 0.3s",
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                }}
                onClick={() => {
                  markReadStatus(notification.id);
                  handleNotificationClick(notification);
                }}
              >
                {/* Notification Icon */}
                <Box sx={{ color: "text.disabled", mt: 0.5 }}>
                  {notification.type === "document" ? (
                    <InsertDriveFileIcon sx={{ color: "gray" }} />
                  ) : (
                    <NotificationsActiveIcon sx={{ color: "gray" }} />
                  )}
                </Box>

                {/* Notification Content */}
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      variant="body1"
                      fontWeight={notification.isRead ? "normal" : "bold"}
                      sx={{ flexGrow: 1 }}
                    >
                      {notification.title}
                    </Typography>
                    {!notification.isRead && (
                      <Badge
                        color="error"
                        variant="dot"
                        sx={{ "& .MuiBadge-badge": { fontSize: 8 } }}
                      />
                    )}
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "pre-wrap", color: "text.secondary" }}
                  >
                    {notification.text}
                  </Typography>

                  <Typography variant="caption" color="textSecondary">
                    {moment(notification.createdAt).fromNow()}
                  </Typography>
                </Box>
              </Box>
            ))
          ) : (
            <MenuItem disabled>
              <Typography
                variant="body1"
                sx={{ textAlign: "center", width: "100%" }}
              >
                No Notifications
              </Typography>
            </MenuItem>
          )}
        </Box>
      </Menu>
    </>
  );
}
